/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import Page from "components/common_components/Page/Page";
import Form from "./Form";
import { useDispatch } from "react-redux";
import { notifySuccess, notifyError, notifyInfo } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Icon from "components/common_components/Icon";
import Button from "components/common_components/Button";
import SettingsContext from "components/Settings/SettingsContext";
import { hasElements } from "utils/arrays";

const headCells = [
    { index: 1, id: "id", label: "Identifier" },
    { index: 2, id: "name", label: "Name" },
    { index: 3, id: "description", label: "Description" },
];

function Groups(props) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [resources, setResources] = useState({ evidenceTypes: [] });
    const [mode, setMode] = useState("listOnly");
    const [itemToEdit, setItemToEdit] = useState(undefined);
    const [pageableData, setPageableData] = useState(undefined);

    const { Groups, Permissions } = useContext(SettingsContext);

    function getGroups(requestData, showLoader = true) {
        if (showLoader) {
            setIsLoading(true);
        }
        Groups.getList({
            onSuccess: (data, pageableData) => {
                setData(data);
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onInfo: (info, pageableData) => {
                dispatch(notifyInfo(info));
                setPageableData(pageableData);
                setIsLoading(false);
            },
            onError: (error) => {
                dispatch(notifyError(error));
                setIsLoading(false);
            },
            data: requestData,
        });
    }

    function getPermissions() {
        setIsLoading(true);
        Permissions.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({
                    ...prev,
                    permissions: data,
                }));
                setIsLoading(false);
            },
            onError: (error) => {
                setResources((prev) => ({
                    ...prev,
                    permissions: [],
                }));
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    useEffect(() => {
        getPermissions();

        return () => {
            setData([]);
            setItemToEdit(undefined);
        };
    }, []);

    useEffect(() => {
        if (hasElements(resources.permissions)) {
            getGroups();
        }
    }, [resources.permissions]);

    function onRowClickHandler(item) {
        setItemToEdit(item);
        setMode("update");
    }

    function onCancelHandler() {
        setItemToEdit(undefined);
        setMode("listOnly");
    }

    function onSuccess(_, setSubmitting) {
        setSubmitting(false);
        onCancelHandler();
        dispatch(notifySuccess(`Group ${mode === "add" ? "created" : "updated"} successfully!`));
        getGroups();
    }

    function onError(error, setSubmitting, setErrors) {
        if (error && error.errors) {
            setErrors(error.errors);
            dispatch(notifyError(error.message));
        } else {
            onCancelHandler();
            dispatch(notifyError(error));
        }
        setSubmitting(false);
    }

    function onSubmitHandler(values, { setSubmitting, setErrors }) {
        const { id, name, permissions } = values;

        const apiCall = mode === "add" ? Groups.add : Groups.update;

        apiCall({
            payload: { id: id.trim().toLowerCase(), name, permissions },
            onSuccess: (response) => onSuccess(response, setSubmitting),
            onError: (error) => onError(error, setSubmitting, setErrors),
        });
    }

    function onDeleteSuccess() {
        dispatch(notifyInfo("Removed successfully!"));
        getGroups();
    }

    function onDeleteError(error) {
        dispatch(notifyError(error.message));
    }

    function onDeleteHandler(selectedItems) {
        Groups.deleteAll({
            ids: selectedItems,
            onSuccess: onDeleteSuccess,
            onError: onDeleteError,
        });
    }

    function filterBuilder(filterValue) {
        return filterValue
            ? {
                  id: filterValue,
                  name: filterValue,
                  description: filterValue,
              }
            : {};
    }

    function onAddHandler() {
        setItemToEdit(undefined);
        setMode("add");
    }

    return (
        <Page className="groups-page">
            {isLoading ? (
                <LoadingPage />
            ) : (
                <>
                    {mode !== "listOnly" && (
                        <Form
                            data={itemToEdit}
                            resources={resources}
                            mode={mode}
                            onSubmit={onSubmitHandler}
                            onCancel={onCancelHandler}
                            title={mode === "add" ? "New Group" : "Update Group"}
                        />
                    )}
                    {mode === "listOnly" && (
                        <>
                            <Button primary onClick={onAddHandler}>
                                <Icon name="add" size="md" color="white" />
                                <span>Add</span>
                            </Button>
                            <EnhancedTable
                                headCells={headCells}
                                data={data}
                                defaultOrder={{
                                    order: "asc",
                                    orderBy: "id",
                                }}
                                selectable
                                keyColumnName="id"
                                onRowClick={onRowClickHandler}
                                onDelete={onDeleteHandler}
                                onRequestData={getGroups}
                                filterBuilder={filterBuilder}
                                pageableData={pageableData}
                                defaultRowsPerPage={10}
                            />
                        </>
                    )}
                </>
            )}
        </Page>
    );
}

export default Groups;
